import { useQuery } from '@tanstack/react-query'
import { CalendarDays } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { Alert } from '@/components/feedback/Alert'
import { FieldError } from '@/components/feedback/FieldError'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { dateInputToLocalDate, localDateToDateInput } from '@/lib/calendar-date'
import { formatDateOnly, todayAsDateInput } from '@/lib/format'
import { isTimeOfDay, normalizeTime } from '@/lib/hr'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { hrApi, type HrRequestCheckRead } from '@/services/http/hr'
import { DetailGrid } from '@/features/hr/parts'
import {
  formatLeaveRange,
  leaveValid,
  leaveValues,
  sameLeave,
  type LeaveFormValue,
  type LeaveValues,
  type MovementValue,
} from '@/features/hr/request-value'

/** Talep formlarının alanları: yeni talep, yeniden gönderme ve onay penceresi ortak. */

const t = messages.hr
const r = t.approvals

/** Form alan hataları (`useRecordEditor`: doğrulama ya da sunucunun 422'si); alanın altında görünür. */
export type FieldErrors<T> = Partial<Record<keyof T, string | undefined>>

/** Takvimden tarih seçimi ("YYYY-MM-DD"). */
function DateField({
  id,
  label,
  value,
  maxDate,
  error,
  onChange,
}: {
  id: string
  label: string
  value: string
  maxDate?: string
  error?: string | null
  onChange: (next: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              id={id}
              variant="secondary"
              className="w-40"
              aria-invalid={!!error}
              aria-describedby={error ? `${id}-error` : undefined}
            />
          }
        >
          <CalendarDays />
          {formatDateOnly(value)}
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2">
          <Calendar
            value={dateInputToLocalDate(value)}
            maxDate={maxDate ? dateInputToLocalDate(maxDate) : undefined}
            weekStartsOn={1}
            onChange={(date) => {
              onChange(localDateToDateInput(date))
              setOpen(false)
            }}
          />
        </PopoverContent>
      </Popover>
      <FieldError id={id} message={error} />
    </div>
  )
}

/** Saat: "8:00", "800" gibi yazımlar alandan çıkınca "08:00" olur; hata alanın altında. */
function TimeField({
  id,
  label,
  value,
  error,
  onChange,
}: {
  id: string
  label: string
  value: string
  /** Sunucu ön kontrolünün bu alana düşen hatası (ör. gelecek saat). */
  error?: string | null
  onChange: (next: string) => void
}) {
  const [touched, setTouched] = useState(false)
  const invalid = value.trim() !== '' && !isTimeOfDay(normalizeTime(value))
  const message = (touched && invalid ? r.timeInvalid : null) ?? error ?? null
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="w-24 tabular-nums"
        inputMode="numeric"
        placeholder="08:00"
        maxLength={5}
        aria-invalid={(touched && invalid) || !!error}
        aria-describedby={message ? `${id}-error` : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => {
          setTouched(true)
          const normalized = normalizeTime(value)
          if (normalized !== value) onChange(normalized)
        }}
      />
      {message && (
        <span id={`${id}-error`} className="text-danger max-w-56 text-xs">
          {message}
        </span>
      )}
    </div>
  )
}

/** Ön kontrolün uyarıları: gönderimi engellemez, kullanıcı görsün diye form içinde. */
function CheckWarnings({ check }: { check: HrRequestCheckRead | null }) {
  if (!check || check.warnings.length === 0) return null
  return (
    <Alert tone="warning" title={r.checkWarnings}>
      <ul className="flex flex-col gap-1">
        {check.warnings.map((warning) => (
          <li key={warning.code}>{warning.message}</li>
        ))}
      </ul>
    </Alert>
  )
}

/** Tip (G/Ç), tarih (takvim), saat (SS:DD) ve açıklama — yeni talep ve yeniden gönderme ortak. */
export function MovementFields({
  idPrefix,
  value,
  check = null,
  errors,
  onChange,
}: {
  idPrefix: string
  value: MovementValue
  /** Sunucu ön kontrolü (`useMovementCheck`): zaman hatası saatin altında, uyarılar altta. */
  check?: HrRequestCheckRead | null
  /** Form hataları (`useRecordEditor`); saat hatası ön kontrolünkünden önce gelir. */
  errors?: FieldErrors<MovementValue>
  onChange: (next: MovementValue) => void
}) {
  const timeError =
    errors?.time ?? check?.errors.find((issue) => issue.field === 'occurred_at')?.message
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label>{r.type}</Label>
        <ToggleGroup
          variant="outline"
          size="sm"
          aria-label={r.type}
          value={[value.girisCikis]}
          onValueChange={([next]) => {
            if (next === 'G' || next === 'Ç') onChange({ ...value, girisCikis: next })
          }}
        >
          <ToggleGroupItem value="G">{t.girisCikis.G}</ToggleGroupItem>
          <ToggleGroupItem value="Ç">{t.girisCikis.Ç}</ToggleGroupItem>
        </ToggleGroup>
      </div>
      <div className="flex flex-wrap gap-4">
        <DateField
          id={`${idPrefix}-date`}
          label={r.date}
          value={value.date}
          maxDate={todayAsDateInput()}
          error={errors?.date}
          onChange={(date) => onChange({ ...value, date })}
        />
        <TimeField
          id={`${idPrefix}-time`}
          label={r.time}
          value={value.time}
          error={timeError}
          onChange={(time) => onChange({ ...value, time })}
        />
      </div>
      <CheckWarnings check={check} />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-description`}>{r.descriptionLabel}</Label>
        <Textarea
          id={`${idPrefix}-description`}
          value={value.description}
          maxLength={2000}
          placeholder={r.descriptionPlaceholder}
          aria-invalid={!!errors?.description}
          aria-describedby={errors?.description ? `${idPrefix}-description-error` : undefined}
          onChange={(event) => onChange({ ...value, description: event.target.value })}
        />
        <FieldError id={`${idPrefix}-description`} message={errors?.description} />
      </div>
    </div>
  )
}

/** İzin türü (DİA'da karşılığı olanlar), saatlik/tam gün, başlangıç ve bitiş, açıklama. */
export function LeaveFields({
  idPrefix,
  value,
  current,
  errors,
  onChange,
}: {
  idPrefix: string
  value: LeaveFormValue
  current: LeaveValues | null
  errors?: FieldErrors<LeaveFormValue>
  onChange: (next: LeaveFormValue) => void
}) {
  const types = useQuery({ queryKey: queryKeys.hr.leaveTypes(), queryFn: hrApi.leaveTypes })
  const options = [...new Set([...(types.data ?? []), value.tur].filter(Boolean))].map((tur) => ({
    value: tur,
    label: tur,
  }))
  const unchanged = current !== null && leaveValid(value) && sameLeave(leaveValues(value), current)
  return (
    <div className="flex flex-col gap-4">
      {current && (
        <p className="text-text-secondary text-sm">
          {r.leave.current}: {current.tur} · {formatLeaveRange(current)}
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-type`}>{r.leave.type}</Label>
        <OptionSelect
          id={`${idPrefix}-type`}
          className="w-64"
          value={value.tur}
          options={options}
          aria-label={r.leave.type}
          onValueChange={(tur) => onChange({ ...value, tur })}
        />
        <span className="text-text-muted text-xs">{r.leave.typeHint}</span>
        <FieldError id={`${idPrefix}-type`} message={errors?.tur} />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Switch
          checked={value.saatlik}
          onCheckedChange={(saatlik) =>
            onChange({ ...value, saatlik, endDate: saatlik ? value.startDate : value.endDate })
          }
        />
        {r.leave.hourly}
      </label>
      <div className="flex flex-wrap gap-4">
        <DateField
          id={`${idPrefix}-start`}
          label={value.saatlik ? r.date : r.leave.start}
          value={value.startDate}
          error={errors?.startDate}
          onChange={(startDate) =>
            onChange({
              ...value,
              startDate,
              endDate: value.saatlik || value.endDate < startDate ? startDate : value.endDate,
            })
          }
        />
        {value.saatlik ? (
          <>
            <TimeField
              id={`${idPrefix}-start-time`}
              label={r.leave.startTime}
              value={value.startTime}
              error={errors?.startTime}
              onChange={(startTime) => onChange({ ...value, startTime })}
            />
            <TimeField
              id={`${idPrefix}-end-time`}
              label={r.leave.endTime}
              value={value.endTime}
              error={errors?.endTime}
              onChange={(endTime) => onChange({ ...value, endTime })}
            />
          </>
        ) : (
          <DateField
            id={`${idPrefix}-end`}
            label={r.leave.end}
            value={value.endDate}
            error={errors?.endDate}
            onChange={(endDate) => onChange({ ...value, endDate })}
          />
        )}
      </div>
      {unchanged && <p className="text-text-muted text-xs">{r.leave.unchanged}</p>}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-description`}>{r.descriptionLabel}</Label>
        <Textarea
          id={`${idPrefix}-description`}
          value={value.description}
          maxLength={2000}
          placeholder={r.leave.descriptionPlaceholder}
          aria-invalid={!!errors?.description}
          aria-describedby={errors?.description ? `${idPrefix}-description-error` : undefined}
          onChange={(event) => onChange({ ...value, description: event.target.value })}
        />
        <FieldError id={`${idPrefix}-description`} message={errors?.description} />
      </div>
    </div>
  )
}

/**
 * Hedef sisteme gitmeden önce son bakış: "Onaysız işle"de ve onayda talebin özeti gösterilir,
 * kullanıcı onaylarsa gönderilir.
 */
export function SubmitConfirm({
  open,
  title,
  body,
  lines,
  pending,
  confirmLabel,
  onOpenChange,
  onConfirm,
}: {
  open: boolean
  title: string
  body: string
  lines: [string, ReactNode][]
  pending: boolean
  confirmLabel: string
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <DetailGrid items={lines} />
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t.actions.cancel}
          </Button>
          <Button disabled={pending} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
